import { arrivalKeepFor, DEFAULT_ARRIVAL, type ArrivalKeep, type ArrivalPolicy } from '@agavi/blocks';
import { useTranslation } from 'react-i18next';

import type { Block } from '@/types/experience';

import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { getBlockLabel, isGradableBlockType } from '@/lib/flow-utils';

/**
 * WHAT A RETURNING STUDENT FINDS, authored.
 *
 * Shown only on a task a student can get back to. On a linear experience there is no return, so
 * every control here would be a decision about something that cannot happen — and an inert
 * control is worse than an absent one, because it invites a teacher to reason about it.
 *
 * TWO SWITCHES, NOT THREE NAMED MODES. "Fresh", "resume" and "resume everything" read as a tidy
 * enumeration and quietly rule out the fourth combination: clear the answers but keep the
 * scaffolding an earlier Change turned on, which is a real thing to want on a second pass.
 *
 * ## ONLY WHAT IS ACTUALLY DECIDABLE
 *
 * The same inert-control rule applies per cell, not just to the section. A text or image block
 * has no answer to show again; a block no Change touches has nothing to keep applied. Both drew
 * a switch anyway in the first spelling, so a task that changes nothing printed a column of
 * dead "keep changes" toggles — which reads as a list of settings a teacher has to go and turn
 * off. An inapplicable cell is EMPTY rather than disabled: a disabled switch still reads as a
 * switch that is off, which is the same miscommunication one step quieter.
 *
 * A block with nothing decidable in either column is not listed at all, and a column with
 * nothing decidable anywhere is not drawn.
 *
 * ## A COLUMN, not a labelled line each
 *
 * The question is the same for every row, so it is asked ONCE at the top of a column and each
 * row is a block name and its switches. Stacking two captioned lines per block printed the same
 * two sentences once per block down a 370px panel and buried the navigation ladder under it.
 * The headers carry the sense of the switches — checked is what the header says.
 *
 * ## What this does NOT cover
 *
 * Variables, question versions and scenario rows. Those live in `session.rolls`, which is drawn
 * once per task per RUN and never redrawn, so they survive a return already and no switch here
 * can change that — see `rollFor`. Deliberate: a student sent to remediation and back has to
 * meet the same question they got wrong.
 */
export function FlowArrivalSection({ policy, blocks, changedBlocks, onChange }: {
  policy: ArrivalPolicy | undefined;
  blocks: Block[];
  /** Blocks some Change in this task touches — see `blocksChangedBy`. */
  changedBlocks: Set<string>;
  onChange: (next: ArrivalPolicy, undoLabel: string) => void;
}) {
  const { t } = useTranslation();
  const current = policy ?? DEFAULT_ARRIVAL;
  const undo = t('task.flow.undo.setArrival');

  const set = (next: Partial<ArrivalPolicy>) =>
    onChange({ ...current, rules: current.rules, ...next }, undo);

  /** Writes one block's row, creating it the first time that block is touched. */
  const setRow = (blockId: string, patch: Partial<ArrivalKeep>) => {
    const existing = current.rules.find((r) => r.blockId === blockId);
    const row = { blockId, ...arrivalKeepFor(current, blockId), ...patch };
    set({
      rules: existing
        ? current.rules.map((r) => (r.blockId === blockId ? row : r))
        : [...current.rules, row],
    });
  };

  const relevant = blocks
    .map((b, i) => ({ block: b, name: getBlockLabel(b, i, t), answers: isGradableBlockType(b), effects: changedBlocks.has(b.id) }))
    .filter((r) => r.answers || r.effects);

  const anyAnswers = relevant.some((r) => r.answers);
  const anyEffects = relevant.some((r) => r.effects);
  if (relevant.length === 0) return null;

  /*
   * Splitting one row produces the same switches under an extra layer of UI, which is the reason
   * the sensor rungs withhold it too. Counted over the RELEVANT rows: a task with six display
   * blocks and one input has one decision, however many blocks it holds.
   */
  const splitToggle = relevant.length > 1 ? (
    <Button variant='subtle' size='sm' className='-my-1 -ml-2 shrink-0' onClick={() => set({ split: !current.split })}>
      {current.split ? t('task.flow.arrival.collapse') : t('task.flow.arrival.split')}
    </Button>
  ) : null;

  const rows = current.split
    ? relevant.map((r) => ({
      key: r.block.id,
      name: r.name,
      named: r.name,
      keep: arrivalKeepFor(current, r.block.id),
      shows: { answers: r.answers, effects: r.effects },
      write: (p: Partial<ArrivalKeep>) => setRow(r.block.id, p),
    }))
    : [{
      key: '*',
      name: t('task.flow.arrival.everyBlock'),
      named: undefined,
      keep: current.keep,
      shows: { answers: anyAnswers, effects: anyEffects },
      write: (p: Partial<ArrivalKeep>) => set({ keep: { ...current.keep, ...p } }),
    }];

  return (
    /* No row gap, padding on the cells instead: a bare switch is a 20px track, and rows spaced
       by a gap alone both crowded the eye and left a tap target half the size the rest of the
       panel uses. The padding does both jobs. */
    <div className='grid grid-cols-[minmax(0,1fr)_repeat(var(--cols),auto)] items-center gap-x-3 rounded-lg border border-ws-border px-3 py-2'
      style={{ '--cols': (anyAnswers ? 1 : 0) + (anyEffects ? 1 : 0) } as React.CSSProperties}>
      {/* The toggle sits in the column it governs — which blocks the rows are about. */}
      <div className='flex min-w-0 items-center'>{splitToggle}</div>
      {anyAnswers && <ColumnHead text={t('task.flow.arrival.showLastAttempt')} />}
      {anyEffects && <ColumnHead text={t('task.flow.arrival.keepChanges')} />}

      {rows.map(({ key, name, named, keep, shows, write }) => (
        <Row key={key} name={name} named={named} keep={keep} shows={shows}
          columns={{ answers: anyAnswers, effects: anyEffects }} onChange={write} />
      ))}
    </div>
  );
}

/** `aria-hidden`: every switch below carries the same words in its own accessible name. */
function ColumnHead({ text }: { text: string }) {
  return (
    <span aria-hidden='true' className='w-[4.5rem] text-center text-[10px] font-medium uppercase leading-tight tracking-wide text-ws-muted'>
      {text}
    </span>
  );
}

/**
 * One block's row, or the consolidated one.
 *
 * `columns` is which columns the TABLE has; `shows` is which of them this row can answer. A cell
 * in a drawn column that this row cannot answer is an empty placeholder, so the grid stays
 * aligned without putting a dead control in it.
 *
 * `named` is the block the row is about, folded into each switch's accessible name. Split there
 * are two switches per block and a dozen reading alike is a list a screen-reader user cannot
 * navigate; consolidated there is one pair, so the plain name is the clearer thing to hear.
 */
function Row({ name, keep, named, shows, columns, onChange }: {
  name: string;
  keep: ArrivalKeep;
  named?: string;
  shows: { answers: boolean; effects: boolean };
  columns: { answers: boolean; effects: boolean };
  onChange: (patch: Partial<ArrivalKeep>) => void;
}) {
  const { t } = useTranslation();
  const cell = (on: boolean, label: string, write: (next: boolean) => void) => (
    <span className='flex w-[4.5rem] justify-center'>
      <Switch className='px-3 py-2' on={on} onChange={write} label={label} />
    </span>
  );
  return (
    <>
      <span className='min-w-0 truncate py-2 text-xs text-ws-text'>{name}</span>
      {columns.answers && (shows.answers
        ? cell(keep.answers, named
          ? t('task.flow.arrival.answersLabelNamed', { block: named })
          : t('task.flow.arrival.answersLabel'), (answers) => onChange({ answers }))
        : <span className='w-[4.5rem]' />)}
      {columns.effects && (shows.effects
        ? cell(keep.effects, named
          ? t('task.flow.arrival.effectsLabelNamed', { block: named })
          : t('task.flow.arrival.effectsLabel'), (effects) => onChange({ effects }))
        : <span className='w-[4.5rem]' />)}
    </>
  );
}
